import { InfiniteData } from "react-query";
import { Timeslot } from "types";
import { queryClient } from "../api/queryClient";
import { flattenInfiniteData, Page } from "@/hooks/helper";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardContext, DashboardMode, DashboardScope } from "@/types/Dashboard";

const useTimeslot = (timeslotId: string | null | undefined) => {
    const filters = useDashboardFilterStore((state) => state.filters);

    // TimeslotsId is string | null | undefined because we can`t call hook conditionally,
    // so if the timeslotsId is not passed we just return null.
    if (!timeslotId) {
        return null;
    }

    const selectedTimeslot = queryClient.getQueryData<Timeslot>(["timeslots", timeslotId]);
    const isEquipmentMode = filters.mode === DashboardMode.EQUIPMENT;

    // Return the timeslot if we have found it earlier.
    // We need a timeslot with event data since capacity in dashboard depends on it.
    if (selectedTimeslot?.event) {
        return selectedTimeslot;
    }

    const params: any = {};

    switch (filters.scope) {
        case DashboardScope.RESERVED:
            params.context = DashboardContext.RESERVED;
            break;
        case DashboardScope.AVAILABLE:
            params.context = DashboardContext.AVAILABLE;
            break;
        case DashboardScope.WAITLIST:
            params.context = DashboardContext.WAITLIST;
            break;
        case DashboardScope.ALL:
            params.context = DashboardContext.ALL;
            break;
        default:
            // Intentionally nothing
            break;
    }

    if (isEquipmentMode) {
        params.context = DashboardContext.EQUIPMENT;
    }

    // Get all fetched timeslots with event,unit data expanded for the dashboard capacity.
    const data = queryClient.getQueriesData<InfiniteData<Page<Timeslot>>>([
        "timeslots",
        "collection",
        "infinite",
        params,
    ]);

    for (const [, timeslots] of data) {
        if (timeslots) {
            const allTimeslots = flattenInfiniteData(timeslots);
            const timeslot = allTimeslots.find(({ id }) => id === timeslotId);

            // Find a timeslot in the infinite cache and set a query data for a single slot.
            if (timeslot) {
                queryClient.setQueryData(["timeslots", timeslotId], timeslot);
                return timeslot;
            }
        }
    }

    return null;
};

export default useTimeslot;
